<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useWindowSize } from '@vueuse/core';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { vOnClickOutside } from '@vueuse/components';
import { toast } from 'dashboard/composables/useToast';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import { debounce } from '@chatwoot/utils';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  searchContacts,
  createNewContact,
  checkWhatsAppNumber,
  fetchContactableInboxes,
  processContactableInboxes,
  mergeInboxDetails,
  normalizeBrazilPhone,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import wootConstants from 'dashboard/constants/globals';

import ComposeNewConversationForm from 'dashboard/components-next/NewConversation/components/ComposeNewConversationForm.vue';

const props = defineProps({
  alignPosition: {
    type: String,
    default: 'left',
  },
  contactId: {
    type: String,
    default: null,
  },
  isModal: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();
const { width: windowWidth } = useWindowSize();

const { fetchSignatureFlagFromUISettings } = useUISettings();

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const viewInModal = computed(() => props.isModal || isSmallScreen.value);

const contacts = ref([]);
const selectedContact = ref(null);
const targetInbox = ref(null);
const isCreatingContact = ref(false);
const isFetchingInboxes = ref(false);
const isSearching = ref(false);
const showComposeNewConversation = ref(false);
const whatsappVerification = ref(null);
const isCheckingWhatsapp = ref(false);

const contactById = useMapGetter('contacts/getContactById');
const contactsUiFlags = useMapGetter('contacts/getUIFlags');
const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');
const uiFlags = useMapGetter('contactConversations/getUIFlags');
const messageSignature = useMapGetter('getMessageSignature');
const inboxesList = useMapGetter('inboxes/getInboxes');

const sendWithSignature = computed(() =>
  fetchSignatureFlagFromUISettings(targetInbox.value?.channelType)
);

const directUploadsEnabled = computed(
  () => globalConfig.value.directUploadsEnabled
);

const activeContact = computed(() => contactById.value(props.contactId));

const composePopoverClass = computed(() => {
  if (viewInModal.value) return '';

  return props.alignPosition === 'right'
    ? 'absolute ltr:left-0 ltr:right-[unset] rtl:right-0 rtl:left-[unset]'
    : 'absolute rtl:left-0 rtl:right-[unset] ltr:right-0 ltr:left-[unset]';
});

const dinastiaInbox = computed(() =>
  inboxesList.value?.find(
    i =>
      i.channel_type === 'Channel::DinastiaWhatsapp' &&
      i.connection_status === 'connected'
  )
);

const onContactSearch = debounce(
  async query => {
    isSearching.value = true;
    contacts.value = [];
    whatsappVerification.value = null;
    isCheckingWhatsapp.value = false;
    try {
      contacts.value = await searchContacts(query);
      // If no contacts found and input looks like phone, verify WhatsApp
      const q = query.query || '';
      const digits = q.replace(/[^\d]/g, '');
      const looksLikePhone =
        q.startsWith('+') ||
        (digits.length >= 8 && digits.length === q.trim().length);
      if (!contacts.value.length && looksLikePhone && dinastiaInbox.value) {
        isCheckingWhatsapp.value = true;
        const phoneToCheck = normalizeBrazilPhone(q);
        const result = await checkWhatsAppNumber(
          phoneToCheck,
          currentUser.value.account_id,
          dinastiaInbox.value.id
        );
        whatsappVerification.value = result;
        isCheckingWhatsapp.value = false;
      }
    } catch (error) {
      toast.error(t('COMPOSE_NEW_CONVERSATION.CONTACT_SEARCH.ERROR_MESSAGE'));
    } finally {
      isSearching.value = false;
      isCheckingWhatsapp.value = false;
    }
  },
  300,
  false
);

const resetContacts = () => {
  contacts.value = [];
};

const handleSelectedContact = async ({ value, action, ...rest }) => {
  let contact;
  if (action === 'create') {
    isCreatingContact.value = true;
    try {
      const verification = whatsappVerification.value;
      const isRegistered = verification?.registered === true;

      // Only trust verified_phone when the number is actually registered on
      // WhatsApp. When registered=false, Dinastia returns verified_phone as
      // an "alternative attempt" (e.g. BR mobile stripped of the leading 9)
      // which is NOT a confirmed number. In that case we normalize the raw
      // input via BR normalizer so the contact can still be pre-created.
      const phoneToUse =
        isRegistered && verification.verified_phone
          ? verification.verified_phone
          : normalizeBrazilPhone(value);

      contact = await createNewContact(phoneToUse);

      // Warn when contact was created but the number does not have WhatsApp.
      if (!isRegistered) {
        toast.warning(
          t('COMPOSE_NEW_CONVERSATION.CONTACT_SEARCH.NOT_ON_WHATSAPP_WARNING')
        );
      }

      isCreatingContact.value = false;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        '[ComposeConversation] createNewContact failed:',
        error?.response?.data || error?.message || error
      );
      isCreatingContact.value = false;
      toast.error(
        t('COMPOSE_NEW_CONVERSATION.CONTACT_SEARCH.ERROR_CREATING_CONTACT')
      );
      return;
    }
  } else {
    contact = rest;
  }
  selectedContact.value = contact;
  if (contact?.id) {
    isFetchingInboxes.value = true;
    try {
      const contactableInboxes = await fetchContactableInboxes(contact.id);
      // Merge the processed contactableInboxes with the inboxesList
      selectedContact.value.contactInboxes = mergeInboxDetails(
        contactableInboxes,
        inboxesList.value
      );

      isFetchingInboxes.value = false;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        '[ComposeConversation] fetchContactableInboxes failed:',
        error?.response?.data || error?.message || error
      );
      isFetchingInboxes.value = false;
    }
  }
};

const handleTargetInbox = inbox => {
  targetInbox.value = inbox;
  resetContacts();
};

const clearSelectedContact = () => {
  selectedContact.value = null;
  targetInbox.value = null;
};

const closeCompose = () => {
  showComposeNewConversation.value = false;
  if (!props.contactId) {
    // If contactId is passed as prop
    // Then don't allow to remove the selected contact
    selectedContact.value = null;
  }
  targetInbox.value = null;
  resetContacts();
  emit('close');
};

const createConversation = async ({ payload, isFromWhatsApp }) => {
  try {
    const data = await store.dispatch('contactConversations/create', {
      params: payload,
      isFromWhatsApp,
    });
    closeCompose();
    toast.success(t('COMPOSE_NEW_CONVERSATION.FORM.SUCCESS_MESSAGE'), {
      action: {
        label: t('COMPOSE_NEW_CONVERSATION.FORM.GO_TO_CONVERSATION'),
        // A rota usa o numero de exibicao, nao o id interno.
        to: `/app/accounts/${data.account_id}/conversations/${data.display_id ?? data.id}`,
      },
    });
    return true; // Return success
  } catch (error) {
    toast.error(
      error instanceof ExceptionWithMessage
        ? error.data
        : t('COMPOSE_NEW_CONVERSATION.FORM.ERROR_MESSAGE')
    );
    return false; // Return failure
  }
};

const toggle = () => {
  showComposeNewConversation.value = !showComposeNewConversation.value;
};

watch(
  activeContact,
  (currentContact, previousContact) => {
    if (currentContact && props.contactId) {
      // Reset on contact change
      if (currentContact?.id !== previousContact?.id) clearSelectedContact();

      // First process the contactable inboxes to get the right structure
      const processedInboxes = processContactableInboxes(
        currentContact.contactInboxes || []
      );
      // Then Merge processedInboxes with the inboxes list
      selectedContact.value = {
        ...currentContact,
        contactInboxes: mergeInboxDetails(processedInboxes, inboxesList.value),
      };
    }
  },
  { immediate: true, deep: true }
);

const handleClickOutside = () => {
  if (!showComposeNewConversation.value) return;

  showComposeNewConversation.value = false;
  emit('close');
};

const onModalBackdropClick = () => {
  if (!viewInModal.value) return;
  handleClickOutside();
};

onMounted(() => resetContacts());

const keyboardEvents = {
  Escape: {
    action: () => {
      if (showComposeNewConversation.value) {
        showComposeNewConversation.value = false;
        emit('close');
        emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
      }
    },
  },
};

useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div
    v-on-click-outside="[
      handleClickOutside,
      // Fixed and edge case https://github.com/chatwoot/chatwoot/issues/10785
      // This will prevent closing the compose conversation modal when the editor Create link popup is open
      { ignore: ['div.ProseMirror-prompt'] },
    ]"
    class="relative"
    :class="{
      'z-50': showComposeNewConversation && !viewInModal,
    }"
  >
    <slot
      name="trigger"
      :is-open="showComposeNewConversation"
      :toggle="toggle"
    />
    <div
      v-if="showComposeNewConversation"
      :class="{
        'fixed z-50 bg-n-alpha-black1 backdrop-blur-[4px] flex items-start pt-[clamp(3rem,15vh,12rem)] justify-center inset-0':
          viewInModal,
      }"
      @click.self="onModalBackdropClick"
    >
      <ComposeNewConversationForm
        :class="[{ 'mt-2': !viewInModal }, composePopoverClass]"
        :contacts="contacts"
        :contact-id="contactId"
        :is-loading="isSearching"
        :current-user="currentUser"
        :selected-contact="selectedContact"
        :target-inbox="targetInbox"
        :is-creating-contact="isCreatingContact"
        :is-fetching-inboxes="isFetchingInboxes"
        :is-direct-uploads-enabled="directUploadsEnabled"
        :contact-conversations-ui-flags="uiFlags"
        :contacts-ui-flags="contactsUiFlags"
        :message-signature="messageSignature"
        :send-with-signature="sendWithSignature"
        :whatsapp-verification="whatsappVerification"
        :is-checking-whatsapp="isCheckingWhatsapp"
        @search-contacts="onContactSearch"
        @reset-contact-search="resetContacts"
        @update-selected-contact="handleSelectedContact"
        @update-target-inbox="handleTargetInbox"
        @clear-selected-contact="clearSelectedContact"
        @create-conversation="createConversation"
        @discard="closeCompose"
      />
    </div>
  </div>
</template>
